import { Fragment, useId, useMemo, useState } from "react";
import {
  defaultOnOf,
  drivenOptionOf,
  labelOf,
  needsOf,
  visibleSettings,
} from "../settings/catalog";
import { useSettings } from "../settings/context";
import { placeOf } from "../settings/search";
import { ORDER_KEY, orderedEntries, writeOrder } from "../funnel/order";
import type { Group as GroupModel, Setting } from "../settings/types";
import {
  childLabel,
  childrenBySwitch,
  keysOf,
  linesOf,
  sectionOf,
  type Line,
} from "../settings/layout";
import { Chips } from "./Chips";
import { ContrastMeter } from "./ContrastMeter";
import { Row } from "./Row";
import { useInPlaceKeys } from "./InPlace";
import { shownIn, useSituation } from "./situation";

/**
 * A COLLAPSIBLE FAMILY OF SETTINGS.
 *
 * "Le site" already carries identity and the footer; it is heading for a dozen
 * families and some two hundred and fifty settings. Everything unfolded at once
 * makes a panel metres long, in which finding a setting means scrolling past
 * every other one.
 *
 * Folded by hand — a button plus `hidden` — rather than `<details>`: the styling
 * stays entirely ours, and trap no. 1 is headed off at the source by
 * `:not([hidden])` in the stylesheet.
 *
 * A family only shows the settings that GOVERN SOMETHING. Switch the footer off
 * and its fields go with it — an editable field that changes nothing on screen is
 * an invitation to waste an afternoon. The count follows, and a line says how
 * many are dormant and why, because fields that vanish without a word read as a
 * bug rather than as a consequence.
 *
 * FOLDED BY DEFAULT. The panel opens as a table of contents — every family, and
 * how many settings each holds — and you unfold the one you came for. With a
 * dozen families on the way, showing everything at once means scrolling to find
 * out what exists at all.
 *
 * What is left open is remembered ACROSS RELOADS: an organiser who works on the
 * footer for an afternoon should not have to unfold it again every time.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/* A NEW key: the old one stored the folded families, this one stores the open
   ones. Reusing the name would have read yesterday's list inside out — every
   family an organiser had tidied away would have come back as the only one open. */
const STORAGE_KEY = "gk-fo-v2-groups-open";

/**
 * LA FAMILLE OUVERTE — une seule, et son titre.
 *
 * ⚠️ LE TABLEAU RESTE, ALORS QU'IL N'EN CONTIENT PLUS QU'UN. La clé a déjà changé une
 * fois pour cause de forme incompatible, et le commentaire au-dessus raconte le dégât :
 * une liste relue à l'envers rouvrait tout ce que l'organisateur avait rangé. En
 * gardant la forme, une mémoire d'hier — plusieurs familles ouvertes — se lit encore,
 * et se réduit d'elle-même à la première. Aucune migration, aucune troisième clé.
 */
export function rememberedOpen(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const titles = raw ? (JSON.parse(raw) as string[]) : [];
    return titles[0] ?? null;
  } catch {
    // Storage unavailable: everything simply starts folded.
    return null;
  }
}

export function rememberOpen(title: string | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(title ? [title] : []));
  } catch {
    /* it will not survive a reload; the panel stays correct */
  }
}

/**
 * NAMING THE SWITCH inside a sentence, whichever shape its label has.
 *
 * A step's switch is called `Afficher « Typologie »`: quoting that again produced
 * « Afficher « Typologie » », nested guillemets that read as a
 * typo, and leaving it bare stumbled. Naming it as a setting carries both shapes.
 */
function named(label: string): string {
  return label.includes("«") ? `le réglage ${label}` : `« ${label} »`;
}

/**
 * ⚠️ L'OUVERTURE EST PILOTÉE PAR LE PANNEAU, plus par la famille elle-même. Une seule
 * reste ouverte à la fois : chacune gardant son propre état, ouvrir la deuxième ne
 * pouvait pas refermer la première — elle n'en savait rien, et rien ne l'aurait
 * redessinée.
 */
export function Group({
  group,
  open,
  onToggle,
  instance,
  aimed = null,
}: {
  group: GroupModel;
  open: boolean;
  onToggle: () => void;
  /** L'instance d'étape répétable qu'on regarde, s'il y en a une. */
  instance?: string;
  /** La clé que la recherche du panneau vient de désigner. */
  aimed?: string | null;
}) {
  const { value, typos, forms } = useSettings();
  const situation = useSituation();
  const inPage = useInPlaceKeys();
  /* Les textes modifiés sur place, repliés ; dépliés à la demande. */
  const [placedOpen, setPlacedOpen] = useState(false);
  const id = useId();

  /**
   * The group's settings, GENERATED when they depend on the event.
   *
   * A typology switch cannot be written into the catalog: its key carries an id
   * that only the event knows. The `null` typology ("every participant") is left
   * out — it is the absence of a typology, not one you can switch off.
   *
   * ⚠️ `forms` FAISAIT PARTIE DU CALCUL SANS ÊTRE DANS LES DÉPENDANCES, et c'est
   * exactement le genre d'oubli qui ne casse rien tant qu'une course se joue dans le bon
   * sens. Les typologies sont attendues par le shell (`ready`), donc elles sont déjà là
   * quand le panneau s'ouvre et ne le redessinent jamais ; les formulaires, eux, arrivent
   * par une requête que personne n'attend. Ouvert avant sa réponse, le panneau figeait
   * une liste d'options VIDE — et le sélecteur « Formulaire servi » annonçait « Cet
   * événement n'en propose aucun » sur un événement qui en porte quinze, sans jamais se
   * corriger.
   */
  /**
   * ⚠️ LE PARCOURS RANGÉ FAIT PARTIE DU CONTEXTE, au même titre que les typologies et les
   * formulaires : la famille « Étapes du parcours » engendre une ligne par étape SERVIE,
   * instances comprises, et seule la configuration sait combien il y en a.
   *
   * ⚠️ ET L'INSTANCE COURANTE AUSSI. Deux étapes « informations » partagent leur écran ;
   * sans elle, le panneau écrirait le titre de la seconde dans les clés de la première.
   */
  const range = orderedEntries(value(ORDER_KEY));

  const settings = useMemo(
    () =>
      typeof group.settings === "function"
        ? group.settings({
            typos: typos.flatMap((t) =>
              t.id === null ? [] : [{ id: t.id, name: t.name }],
            ),
            forms,
            ...(instance ? { instance } : {}),
            steps: range,
          })
        : group.settings,
    // `range` se refait à chaque rendu : c'est sa VALEUR qui compte, pas son identité.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group, typos, forms, instance, writeOrder(range)],
  );

  /* ⚠️ NI MONTRÉES, NI COMPTÉES COMME ENDORMIES : une ligne pilotée par un gestionnaire
     n'est pas cachée par un interrupteur, elle est AILLEURS. Rangée parmi les dormantes,
     la famille aurait annoncé « 18 réglages masqués » sous un parcours parfaitement
     visible. */
  /* La situation choisie dans le panneau écarte ce qui ne se voit pas dans cette version
     de la confirmation (voir `panel/situation.tsx`) : ni montré, ni compté comme
     endormi, puisque aucun interrupteur ne le rendrait visible ici. */
  const rendered = settings.filter(
    (s) => !s.managed && shownIn(s, situation),
  );

  // A setting whose switch is off governs nothing; it goes away rather than sit
  // there looking editable.
  /**
   * ⚠️ LES TEXTES QUE L'ÉCRAN MODIFIE SUR PLACE QUITTENT LA LISTE (voir `InPlace.tsx`) :
   * un titre se change en cliquant dessus dans l'aperçu, et le répéter ici en ligne
   * complète doublait la longueur de la famille. Ils restent atteignables, repliés en
   * une ligne en bas de la famille. Seuls ceux que l'écran AFFICHÉ sait modifier : sur
   * un autre écran, ou dans « Le site », rien ne change.
   */
  const placed = rendered.filter(
    (s) => s.type === "text" && inPage.has(s.key),
  );
  const onPanel = rendered.filter((s) => !placed.includes(s));
  const shown = visibleSettings(onPanel, value);

  /**
   * Les lignes de la famille : un interrupteur et ses textes ensemble, une suite
   * d'interrupteurs en pastilles, un intitulé et son texte côte à côte. Voir
   * `settings/layout.ts`.
   */
  const children = childrenBySwitch(onPanel);
  const lines = linesOf(shown, children);

  /**
   * ⚠️ UN TEXTE RANGÉ SOUS SON INTERRUPTEUR N'EST PAS ANNONCÉ COMME MASQUÉ quand cet
   * interrupteur est à l'écran : on voit l'interrupteur éteint juste au-dessus, la note
   * répéterait ce que la ligne montre. Si c'est l'interrupteur lui-même qui est masqué,
   * le texte compte de nouveau, et la note remonte jusqu'à la cause visible.
   */
  const ownerShown = (setting: Setting) =>
    [...children.entries()].some(
      ([master, kids]) =>
        kids.includes(setting) && shown.some((s) => s.key === master),
    );
  const dormant = onPanel.filter(
    (s) => !shown.includes(s) && !ownerShown(s),
  );

  /**
   * One line PER SWITCH that is off. A family can hold several — the footer's
   * own switch today, a "map" switch tomorrow — and a single line naming the
   * first would leave the others unexplained, which is the same silence the note
   * exists to break.
   *
   * The switch's OWN label, looked up rather than written twice: rename it and
   * the explanation follows instead of quietly going stale.
   */
  /**
   * ⚠️ LA NOTE DÉSIGNE LA CAUSE QU'ON PEUT VOIR. Un réglage peut être masqué par un
   * interrupteur lui-même masqué : « Libellé du lien » dépend de « Lien mot de passe
   * oublié », que « Connexion par lien envoyé par e-mail » vient de retirer de
   * l'écran. Nommer le maillon du milieu envoyait chercher un interrupteur absent du
   * panneau — une explication qui n'explique rien. On remonte donc jusqu'au premier
   * interrupteur encore affiché.
   */
  /**
   * Plusieurs conditions (`needs` en liste) : on nomme celle qui ne tient PAS, et parmi
   * elles la première dont l'interrupteur est encore à l'écran. Le libellé du mot de
   * passe, masqué par « code par e-mail », ne doit pas être expliqué par « lien par
   * e-mail », éteint et donc innocent.
   */
  const failing = (setting: Setting): string => {
    const one = needsOf(setting).find((need) => {
      const negated = need.startsWith("!");
      const key = negated ? need.slice(1) : need;
      const master = rendered.find((m) => m.key === key);
      const raw = value(key);
      const on =
        typeof raw === "boolean"
          ? raw
          : master
            ? master.onByDefault === true
            : defaultOnOf(key);
      const masterHidden = !!master && !shown.includes(master);
      return masterHidden || (negated ? on : !on);
    });
    return one ?? needsOf(setting)[0] ?? "";
  };

  const rootOf = (setting: Setting): { key: string; negated: boolean } => {
    let current = setting;
    for (let depth = 0; depth < 10; depth += 1) {
      const needs = failing(current);
      const negated = needs.startsWith("!");
      const key = negated ? needs.slice(1) : needs;
      const master = rendered.find((m) => m.key === key);
      if (!master || shown.includes(master)) return { key, negated };
      current = master;
    }
    return { key: needsOf(setting)[0] ?? "", negated: false };
  };

  const causes = new Map<
    string,
    {
      label: string;
      negated: boolean;
      option?: string;
      where?: string;
      count: number;
    }
  >();
  for (const setting of dormant) {
    const { key, negated } = rootOf(setting);
    const label =
      settings.find((s) => s.key === key)?.label ?? (key ? labelOf(key) : "");
    if (!label) continue;
    /* ⚠️ RANGÉ PAR LIBELLÉ, PAS PAR CLÉ : le mode de connexion est TROIS clés
       (`LOGIN_OTP`, `LOGIN_AUTO_CONNECT`, `LOGIN_DISABLED`) sous un seul libellé. Par clé,
       l'écran de connexion écrivait deux fois la même note, et React voyait deux lignes
       de même `key`.
       ⚠️ ET UNE CLÉ PILOTÉE PAR UN SÉLECTEUR SE DIT PAR SON OPTION, pas par `negated` :
       les trois clés masquent dans des sens opposés (« !LOGIN_OTP » pour le mot de
       passe, « LOGIN_AUTO_CONNECT » pour l'email du lien), et la première rangée
       décidait pour toutes. L'option choisie, elle, est la même pour les trois et vraie
       pour chacune : c'est elle qui masque. */
    const driven = drivenOptionOf(key, value);
    const option = driven?.option;
    /* ⚠️ UNE CAUSE HORS DE LA FAMILLE SE DIT AVEC SON ADRESSE (bug #294) : « Servie »
       est le libellé de TOUTES les étapes, et la famille « Emails » n'en montre aucune ;
       sans le nom de l'étape et l'endroit où elle se règle, la note n'expliquait rien.
       Rangée par libellé ET adresse : deux étapes éteintes font deux notes. */
    const home = driven?.driver ?? key;
    const where = rendered.some((s) => s.key === home) ? "" : placeOf(home);
    const cause = `${label}|${where}`;
    const known = causes.get(cause);
    if (known) known.count += 1;
    else
      causes.set(cause, {
        label,
        negated,
        ...(option ? { option } : {}),
        ...(where ? { where } : {}),
        count: 1,
      });
  }
  const dormantBy = [...causes.values()];

  /* Une famille dont la situation choisie écarte TOUT (les emails de confirmation, tant
     qu'un paiement reste dû) n'a rien à dire dans cette version de l'écran : elle
     s'efface. */
  if (settings.length > 0 && rendered.length === 0 && situation) return null;

  return (
    <div className="grp">
      <button
        type="button"
        className="grp__t"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className="grp__ch" aria-hidden="true" />
        <span className="grp__l">{group.title}</span>
        {/* The count so a folded family still says how much it holds — otherwise
            folding turns it into a title with nothing behind it. */}
        <span className="grp__n">{lines.length}</span>
      </button>

      <div className="grp__c" id={id} hidden={!open}>
        {group.help && <p className="row__h">{group.help}</p>}
        {/* Derived from the settings themselves, not from the family's title: a
            renamed family would otherwise silently lose its meter. */}
        {shown.some((s) => s.type === "color") && <ContrastMeter />}
        {lines.map((line, i) => {
          const section = sectionOf(line);
          /* Only when it CHANGES, and only over shown lines: a heading whose rows are
             all hidden would be a title with nothing under it. Une grille de pastilles
             porte sa section comme libellé : elle ne la répète pas au-dessus d'elle. */
          const heading =
            line.kind !== "chips" &&
            section &&
            section !== (i > 0 ? sectionOf(lines[i - 1]!) : undefined);
          return (
            <Fragment key={keysOf(line).join(" ")}>
              {heading && <p className="grp__s">{section}</p>}
              <LineView line={line} aimed={aimed} />
            </Fragment>
          );
        })}
        {/* SAY SO. Fields that vanish without a word read as a bug, and the
            organiser goes looking for what they broke. */}
        {placed.length > 0 && (
          <div className="inpage">
            <p className="row__h">
              {placed.length === 1
                ? "1 texte se modifie"
                : `${placed.length} textes se modifient`}{" "}
              en cliquant dessus dans l'aperçu :{" "}
              {placed.map((s) => s.label).join(", ")}.
            </p>
            <button
              type="button"
              className="inpage__t"
              aria-expanded={placedOpen}
              onClick={() => setPlacedOpen((v) => !v)}
            >
              {placedOpen ? "Replier" : "Les modifier ici"}
            </button>
            {(placedOpen || placed.some((s) => s.key === aimed)) &&
              placed.map((s) => (
                <Row key={s.key} setting={s} aimed={aimed === s.key} />
              ))}
          </div>
        )}
        {dormantBy.map(({ label, count, negated, option, where }) => (
          <p className="row__h" key={`${label}|${where ?? ""}`}>
            {count === 1
              ? "1 réglage est masqué"
              : `${count} réglages sont masqués`}{" "}
            tant que {named(label)}{" "}
            {option
              ? `est réglé sur « ${option} »`
              : `est ${negated ? "activé" : "désactivé"}`}
            {where ? ` : il se règle dans ${where}.` : "."}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Une ligne de famille, selon sa forme — voir `settings/layout.ts`. */
function LineView({ line, aimed }: { line: Line; aimed: string | null }) {
  switch (line.kind) {
    case "row":
      return <Row setting={line.setting} aimed={aimed === line.setting.key} />;
    case "pair":
      return (
        <div className="rowset">
          <Row setting={line.setting} aimed={aimed === line.setting.key} />
          {line.children.length > 0 && (
            <div className="rowset__k">
              {line.children.map((child) => (
                <Row
                  key={child.key}
                  setting={child}
                  label={childLabel(child, line.setting)}
                  aimed={aimed === child.key}
                />
              ))}
            </div>
          )}
        </div>
      );
    case "chips":
      return (
        <Chips
          section={line.section}
          settings={line.settings}
          aimed={!!aimed && line.settings.some((s) => s.key === aimed)}
        />
      );
    case "duo":
      return (
        <div className="rowduo">
          <Row setting={line.first} aimed={aimed === line.first.key} />
          <Row setting={line.second} aimed={aimed === line.second.key} />
        </div>
      );
  }
}
